// @ts-check

/**
 * @file Renders the invoice group create/edit form.
 */

import {Form} from "@kaspernj/api-maker/build/form"
import {useLocalSearchParams, useRouter} from "expo-router"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"
import {createRef, memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import buildRoutePath from "../../shared/build-route-path"
import Button from "../../components/button"
import Card from "../../components/card"
import errorMessage from "../../shared/error-message"
import FormTextInput from "../../components/form/form-text-input"
import {InvoiceGroup} from "../../frontend-models/invoice-group"
import {optionalRouteParam} from "../../shared/route-params"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../shared/use-theme-colors"
import {useScreenLoading} from "../../components/board-layout/screen-loading"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{[key: string]: unknown}} InvoiceGroupsEditScreenProps */

/**
 * @typedef {object} InvoiceGroupsEditScreenState
 * @property {InvoiceGroup | null} invoiceGroup Loaded invoice group being edited.
 * @property {boolean} loading Whether invoice group data is loading.
 * @property {boolean} saving Whether form submission is running.
 * @property {{kind: "error" | "success", text: string} | null} status Screen status message.
 */

/**
 * Invoice group edit screen.
 * @augments {ShapeComponent<InvoiceGroupsEditScreenProps, InvoiceGroupsEditScreenState>}
 */
class InvoiceGroupsEditScreen extends ShapeComponent {
  /** @type {string | null} */
  invoiceGroupID
  /** @type {ReturnType<typeof useLocalSearchParams>} */
  params
  /** @type {ReturnType<typeof useRouter>} */
  router
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  formObjectRef = createRef()
  /** @type {InvoiceGroupsEditScreenState} */
  state = {
    invoiceGroup: /** @type {InvoiceGroup | null} */ null,
    loading: true,
    saving: false,
    status:
      /** @type {{kind: "error" | "success", text: string} | null} */ null
  }

  /** Initializes route params and invoice group loading. */
  setup() {
    useLocale()
    useScreenLoading(this.s.loading)
    const params = useLocalSearchParams()
    const router = useRouter()

    const invoiceGroupID = optionalRouteParam(params, "id", "Invoice group ID")
    this.invoiceGroupID = invoiceGroupID
    this.params = params
    this.router = router
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    useEffect(() => {
      let cancelled = false
      const loadData = async () => {
        try {
          if (invoiceGroupID) {
            const invoiceGroup = await InvoiceGroup.find(invoiceGroupID)
            if (cancelled) return
            this.s.invoiceGroup = invoiceGroup
            this.s.loading = false
          } else {
            this.s.loading = false
          }
        } catch (error) {
          if (cancelled) return
          this.s.loading = false
          this.s.status = {
            kind: "error",
            text: errorMessage(error)
          }
        }
      }
      void loadData()
      return () => {
        cancelled = true
      }
    }, [invoiceGroupID])
  }

  /**
   * Renders the invoice group form.
   * @returns {ReactNode} Invoice group form content.
   */
  render() {
    const {invoiceGroup, loading, saving, status} = this.s
    const {invoiceGroupID} = this.tt
    if (loading) {
      return (
        <View
          style={styles.loadingScreen ||= {flex: 1}}
          testID="invoiceGroupEditScreen"
        />
      )
    }
    return (
      <View
        style={styles.screen ||= {
          flex: 1
        }}
        testID="invoiceGroupEditScreen"
      >
        <Text
          style={styles.screenTitle ||= {
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 16
          }}
          testID="invoiceGroupEditScreen/title"
        >
          {invoiceGroupID ? _("Edit invoice group") : _("New invoice group")}
        </Text>
        <Form formObjectRef={this.formObjectRef} onSubmit={this.tt.onSubmit}>
          <Card
            style={styles.formCard ||= {
              gap: 12,
              maxWidth: 520,
              width: "100%"
            }}
            testID="invoiceGroupEditScreen/form"
          >
            <FormTextInput
              defaultValue={invoiceGroup ? invoiceGroup.name() || "" : ""}
              label={_("Name")}
              name="name"
              testID="invoiceGroupEditScreen/nameInput"
            />
            {status &&
              <Text
                style={styles[`status-${this.tt.themeName}-${status.kind}`] ||= {
                  color: status.kind === "success" ? this.tt.colors.successText : this.tt.colors.dangerText,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="invoiceGroupEditScreen/statusMessage"
              >
                {status.text}
              </Text>
            }
            <Button
              disabled={saving}
              iconName="floppy-disk"
              label={saving ? _("Saving...") : _("Save")}
              onPress={this.tt.onSubmit}
              testID="invoiceGroupEditScreen/saveButton"
            />
          </Card>
        </Form>
      </View>
    )
  }

  /**
   * Saves the invoice group form.
   * @returns {Promise<void>} Resolves when save handling completes.
   */
  onSubmit = async () => {
    const {invoiceGroupID, params, router} = this.tt
    const {invoiceGroup} = this.s
    const formObject = this.formObjectRef.current
    if (!formObject) return
    const values = formObject.asObject()
    this.s.saving = true
    this.s.status = null
    try {
      if (invoiceGroup && invoiceGroupID) {
        await invoiceGroup.update({
          name: values.name || ""
        })
        router.push(
          buildRoutePath(params, `/invoice-groups/${invoiceGroupID}`)
        )
      } else {
        const created = await InvoiceGroup.create({
          name: values.name || ""
        })
        router.push(
          buildRoutePath(params, `/invoice-groups/${created.id()}`)
        )
      }
    } catch (error) {
      this.s.saving = false
      this.s.status = {
        kind: "error",
        text: errorMessage(error)
      }
    }
  }
}

/** @type {NamedExoticComponent<InvoiceGroupsEditScreenProps>} */
const InvoiceGroupsEditScreenComponent = memo(shapeComponent(InvoiceGroupsEditScreen))

export default InvoiceGroupsEditScreenComponent
